import { Suspense, lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";

import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";

// Import Business Setup Provider
import BusinessSetupLayout from "../modules/Client/modules/System/Modules/Bussiness-Setup/Components/BusinessSetupLayout";

// Main Component
import App from "../App";

// Guards
import RoleGuard from "./roleguard";
import RedirectRoute from "./redirectroute";
import ProtectedRoute from "./protectedroute";

// Critical Components (Keep eagerly loaded)
import ErrorPage from "../modules/Landing/Pages/ErrorPage.jsx";

// Lazy Load Main Modules
const Landing = lazy(() => import("../modules/Landing/Landing"));
const Client = lazy(() => import("../modules/Client/Client"));
const Admin = lazy(() => import("../modules/Admin/Admin"));
const Moderator = lazy(() => import("../modules/Moderator/Moderator"));

// Lazy Load Landing Pages
const Blog = lazy(() => import("../modules/Landing/Pages/mainBlog.jsx"));
const Main = lazy(() => import("../modules/Landing/Pages/Main.jsx"));
const Plans = lazy(() => import("../modules/Landing/Pages/Plans.jsx"));
const TermsPage = lazy(() => import("../modules/Landing/Pages/TermsPage"));
const ContactPage = lazy(() => import("../modules/Landing/Pages/ContactPage"));
const Features = lazy(() => import("../modules/Landing/Components/Features.jsx"));
const PrivacyPolicy = lazy(() => import("../modules/Landing/Pages/PrivacyPolicy"));
const BlogDetails = lazy(() => import('../modules/Landing/Components/Blogs/BlogDetails'));
const ShoplinePrivacyPolicy = lazy(() => import("../modules/Landing/Pages/ShoplinePrivacyPolicy"));
const ShoplineTermsConditions = lazy(() => import("../modules/Landing/Pages/ShoplineTermsConditions"));
const ShoplineFAQ = lazy(() => import("../modules/Landing/Pages/ShoplineFAQ"));

// Lazy Load Authentication Pages
const Register = lazy(() => import("../modules/Landing/Pages/Register"));
const VerifyPage = lazy(() => import("../modules/Landing/Pages/VerifyPage"));
const Login = lazy(() => import("../modules/Landing/Pages/Login"));
const DemoAccess = lazy(() => import("../modules/Landing/Pages/DemoAccess"));
const ResetPage = lazy(() => import("../modules/Landing/Pages/ResetPage"));
const PasswordReset = lazy(() => import("../modules/Landing/Pages/PasswordReset"));

// Lazy Load Client Modules
const System = lazy(() => import("../modules/Client/modules/System/System"));
const Onboarding = lazy(() => import("../modules/Client/modules/Onboarding/Onboarding"));

// Lazy Load Admin Modules
const AdminUsers = lazy(() => import("../modules/Admin/Modules/AdminUsers/AdminUsers"));
const MainDashboard = lazy(() => import("../modules/Client/modules/System/Modules/Main-Dashboard/MainDashboard"));
const AdminAnalytics = lazy(() => import("../modules/Admin/Modules/DashboardAnalytics/Analystics"));
const QuestionDashboard = lazy(() => import("../modules/Admin/Modules/AdminQuestioners/QuestionDashboard"));
const PromptsDashboard = lazy(() => import("../modules/Admin/Modules/AdminPrompts/PromptsDashboard"));


// Lazy Load Moderator Modules
const ModeratorQuestionnaire = lazy(() => import("../modules/Moderator/Modules/ModeratorQuestionnaire/ModeratorQuestionnaire"));
const ModeratorUsers = lazy(() => import("../modules/Moderator/Modules/ModeratorUsers/ModeratorUsers"));
const ModeratorAnalytics = lazy(() => import("../modules/Moderator/Modules/ModeratorAnalytics/ModeratorAnalystics"));
const ModeratorPromptsDashboard = lazy(() => import("../modules/Moderator/Modules/ModeratorPrompts/ModeratorPromptsDashboard"));
const ModeratorBlogs = lazy(() => import("../modules/Moderator/Modules/ModeratorBlogs/ModeratorBlogs.jsx"));

// Lazy Load System Modules
const Dashboard = lazy(() => import("../modules/Client/modules/System/Modules/Dashboard/Dashboard"));
const Setup = lazy(() => import("../modules/Client/modules/System/Modules/Setup/Setup"));
const Financials = lazy(() => import("../modules/Client/modules/System/Modules/Financials/Financials"));
const Reports = lazy(() => import("../modules/Client/modules/System/Modules/Reports/Reports"));
const UserSettings = lazy(() => import("../modules/Client/modules/System/Modules/UserSetting/UserAccount"));
const Subscription = lazy(() => import("../modules/Client/modules/System/Modules/Subscription/Subscription"));

// Lazy Load Business Setup Pages (NEW)
const BusinessSetupMain = lazy(() => import("../modules/Client/modules/System/Modules/Bussiness-Setup/Pages/SetupPage"));
const MarketAnalysisPage = lazy(() => import("../modules/Client/modules/System/Modules/Bussiness-Setup/Pages/MarketAnalysisPage"));
const BusinessModelPage = lazy(() => import("../modules/Client/modules/System/Modules/Bussiness-Setup/Pages/BusinessModelPage"));
const RevenueForecastPage = lazy(() => import("../modules/Client/modules/System/Modules/Bussiness-Setup/Pages/RevenueForecastPage"));
const FundingPlanPage = lazy(() => import("../modules/Client/modules/System/Modules/Bussiness-Setup/Pages/FundingPlanPage"));
// Lazy Load Bussiness Setup Pages
const FullPlaningPage = lazy(() => import("../modules/Client/modules/System/Modules/Bussiness-Setup/Pages/FullPlaningPage"));
const ExitStrategyPage = lazy(() => import("../modules/Client/modules/System/Modules/Bussiness-Setup/Pages/ExitStrategyPage"));
const ReviewPlanPage = lazy(() => import("../modules/Client/modules/System/Modules/Bussiness-Setup/Pages/ReviewPlanPage"));

const SetupPage = lazy(() => import("./../modules/Client/modules/System/Modules/Setup/Pages/SetupPage"));
const QuestionnerPage = lazy(() => import("../modules/Client/modules/System/Modules/Setup/Pages/QuestionnerPage"));

// Lazy Load Financials Pages
const FinancialReports = lazy(() => import("../modules/Client/modules/System/Modules/Financials/Pages/FinancialReports"));
const FinancialsPage = lazy(() => import("../modules/Client/modules/System/Modules/Financials/Pages/FinancialsPage"));

// Lazy Load Reports Pages
const ReportsPage = lazy(() => import("../modules/Client/modules/System/Modules/Reports/Pages/ReportsPage"));
const CashFlow = lazy(() => import("../modules/Client/modules/System/Modules/Reports/Pages/CashFlow"));
const ProfitLoss = lazy(() => import("../modules/Client/modules/System/Modules/Reports/Pages/ProfitLoss"));
const BalanceSheet = lazy(() => import("../modules/Client/modules/System/Modules/Reports/Pages/BalanceSheet"));

// Lazy Load Subscription Pages
const Checkout = lazy(() => import("../modules/Client/modules/System/Modules/Subscription/Pages/Checkout"));
const AfterCheckout = lazy(() => import("../modules/Client/modules/System/Modules/Subscription/Pages/AfterCheckout"));
const PlansTable = lazy(() => import("../modules/Client/modules/System/Modules/Subscription/Pages/PlansTable"));

// Lazy Load Dashboard Modules
const MainBoard = lazy(() => import('../modules/Client/modules/System/Modules/Dashboard/Modules/Main/MainBoard'));
const Bussiness = lazy(() => import('../modules/Client/modules/System/Modules/Dashboard/Modules/Bussiness/Bussiness'));
const Scenario = lazy(() => import('../modules/Client/modules/System/Modules/Dashboard/Modules/Scenario/Scenario'));
const Useraccess = lazy(() => import("../modules/Client/modules/System/Modules/Dashboard/Modules/Useraccess/Useraccess"));

// Lazy Load Integration Modules
const Integrations = lazy(() => import('../modules/Client/modules/System/Modules/Integrations/Integrations'));
const QuickBooksPage = lazy(() => import('../modules/Client/modules/System/Modules/Integrations/Pages/QuickBooksPage'));

// Lazy Load QuickBooks Reports Modules
const QuickBooksReports = lazy(() => import('../modules/Client/modules/System/Modules/QuickBooksReports/QuickBooksReports'));
const QuickBooksReportsPage = lazy(() => import('../modules/Client/modules/System/Modules/QuickBooksReports/Pages/QuickBooksReportsPage'));
const QuickBooksReportDetailPage = lazy(() => import('../modules/Client/modules/System/Modules/QuickBooksReports/Pages/QuickBooksReportDetailPage'));
const CumulativeReportsOverview = lazy(() => import('../modules/Client/modules/System/Modules/QuickBooksReports/Pages/Cumulative/CumulativeReportsOverview'));
const CumulativeProfitLoss = lazy(() => import('../modules/Client/modules/System/Modules/QuickBooksReports/Pages/Cumulative/ProfitLossReport'));
const CumulativeCashFlow = lazy(() => import('../modules/Client/modules/System/Modules/QuickBooksReports/Pages/Cumulative/CashFlowReport'));
const CumulativeBalanceSheet = lazy(() => import('../modules/Client/modules/System/Modules/QuickBooksReports/Pages/Cumulative/BalanceSheetReport'));


// Lazy Load Dashboard Sub-Pages
const Overview = lazy(() => import("../modules/Client/modules/System/Modules/Dashboard/Modules/Bussiness/Pages/Overview"));
const Form = lazy(() => import("../modules/Client/modules/System/Modules/Dashboard/Modules/Bussiness/Pages/Form"));
const SceOverview = lazy(() => import("../modules/Client/modules/System/Modules/Dashboard/Modules/Scenario/Pages/SceOverview"));
const SceForm = lazy(() => import("../modules/Client/modules/System/Modules/Dashboard/Modules/Scenario/Pages/SceForm"));

// Lazy Load Admin User Pages
const Admins = lazy(() => import("../modules/Admin/Modules/AdminUsers/Pages/Admins"));
const Moderators = lazy(() => import("../modules/Admin/Modules/AdminUsers/Pages/Moderators"));
const Clients = lazy(() => import("../modules/Admin/Modules/AdminUsers/Pages/Clients"));
const ClientStats = lazy(() => import("../modules/Admin/Modules/AdminUsers/Pages/ClientStats"));
const StepDetail = lazy(() => import("../modules/Admin/Modules/AdminUsers/Pages/StepDetail"));

// Lazy Load Moderator User Pages
const ModeratorClients = lazy(() => import("../modules/Moderator/Modules/ModeratorUsers/Pages/ModeratorClients"));
const ModeratorClientStats = lazy(() => import("../modules/Moderator/Modules/ModeratorUsers/Pages/ModeratorClientStats"));
const ModeratorClientStep = lazy(() => import("../modules/Moderator/Modules/ModeratorUsers/Pages/ModeratorStepDetail"));

// Lazy Load Other Admin Sub Pages
const Questionnaire = lazy(() => import("../modules/Admin/Modules/AdminQuestioners/Pages/Questionnaire"));
const ReportsOverview = lazy(() => import("../modules/Client/modules/System/Modules/Overview/ReportsOverview"));
const Prompts = lazy(() => import("../modules/Admin/Modules/AdminPrompts/Pages/Prompts"));

// Lazy Load Other Moderator Sub Pages
const ModeratorQuestions = lazy(() => import("../modules/Moderator/Modules/ModeratorQuestionnaire/Pages/ModeratorQuestions.jsx"));
const ModeratorPrompts = lazy(() => import("../modules/Moderator/Modules/ModeratorPrompts/Pages/ModeratorPrompts"));

// Lazy Load Blog Components
const BlogsDashboard = lazy(() => import("../modules/Admin/Modules/AdminBlogs/BlogsDashboard.jsx"));
const Blogs = lazy(() => import("../modules/Admin/Modules/AdminBlogs/Pages/Blogs.jsx"));

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    children: [
      {
        index: true,
        element: <RedirectRoute />,
      },
      {
        path: "Home",
        element: (
          <ProtectedRoute>
            <Suspense fallback={
              <div className="flex h-screen items-center justify-center">
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                />
              </div>
            }>
              <Landing />
            </Suspense>
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <Main />
              </Suspense>
            )
          },
          {
            path: "register",
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <Register />
              </Suspense>
            )
          },
          {
            path: "verify",
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <VerifyPage />
              </Suspense>
            ),
          },
          {
            path: "login",
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <Login />
              </Suspense>
            )
          },
          {
            path: "demo-access/:userId",
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <DemoAccess />
              </Suspense>
            )
          },
          {
            path: "reset",
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <ResetPage />
              </Suspense>
            ),
          },
          {
            path: "password-reset/:token",
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <PasswordReset />
              </Suspense>
            )
          },
          {
            path: "privacy-and-policy",
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <PrivacyPolicy />
              </Suspense>
            )
          },
          {
            path: "terms-and-conditions",
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <TermsPage />
              </Suspense>
            ),
          },
          {
            path: "plans",
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <Plans />
              </Suspense>
            ),
          },
          {
            path: "features",
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <Features />
              </Suspense>
            ),
          },
          {
            path: "contact-us",
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <ContactPage />
              </Suspense>
            ),
          },
          {
            path: "blogs",
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <Blog />
              </Suspense>
            ),
          },

          {
            path: "blogs/:id",
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <BlogDetails />
              </Suspense>
            ),
          },
          {
            path: "shopline/privacy-policy",
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <ShoplinePrivacyPolicy />
              </Suspense>
            ),
          },
          {
            path: "shopline/terms-conditions",
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <ShoplineTermsConditions />
              </Suspense>
            ),
          },
          {
            path: "shopline/faq",
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <ShoplineFAQ />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "User",
        element: (
          <ProtectedRoute>
            <RoleGuard>
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <Client />
              </Suspense>
            </RoleGuard>
          </ProtectedRoute>
        ),
        children: [
          {
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <System />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="/User/Dashboard" />,
              },
              {
                path: "Dashboard",
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <Dashboard />
                  </Suspense>
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <MainBoard />
                      </Suspense>
                    ),
                  },
                  {
                    path: "Business",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <Bussiness />
                      </Suspense>
                    ),
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={
                            <div className="flex h-screen items-center justify-center">
                              <Spin
                                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                              />
                            </div>
                          }>
                            <Overview />
                          </Suspense>
                        ),
                      },
                      {
                        path: "Form",
                        element: (
                          <Suspense fallback={
                            <div className="flex h-screen items-center justify-center">
                              <Spin
                                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                              />
                            </div>
                          }>
                            <Form />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                  {
                    path: "Scenario",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <Scenario />
                      </Suspense>
                    ),
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={
                            <div className="flex h-screen items-center justify-center">
                              <Spin
                                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                              />
                            </div>
                          }>
                            <SceOverview />
                          </Suspense>
                        ),
                      },
                      {
                        path: "Form",
                        element: (
                          <Suspense fallback={
                            <div className="flex h-screen items-center justify-center">
                              <Spin
                                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                              />
                            </div>
                          }>
                            <SceForm />
                          </Suspense>
                        ),
                      }
                    ]
                  },
                  {
                    path: 'User-access',
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <Useraccess />
                      </Suspense>
                    ),
                  }
                ]
              },
              {
                path: "Financials-setup",
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <Setup />
                  </Suspense>
                ),

                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <SetupPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: ":title",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <QuestionnerPage />
                      </Suspense>
                    )
                  },
                ],
              },
              {
                path: "Financials-data",
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <Financials />
                  </Suspense>
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <FinancialReports />
                      </Suspense>
                    ),
                  },
                  {
                    path: ":title",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <FinancialsPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {

                path: "Onboarding",
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <Onboarding />
                  </Suspense>
                ),
              },

              {
                path: "business-setup",
                element: <BusinessSetupLayout />,
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <BusinessSetupMain />
                      </Suspense>
                    ),
                  },
                  {
                    path: "market-analysis",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <MarketAnalysisPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "business-model",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <BusinessModelPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "revenue-forecast",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <RevenueForecastPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "funding-plan",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <FundingPlanPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "full-plan",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <FullPlaningPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "exit-strategy",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <ExitStrategyPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "review-plan",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <ReviewPlanPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: "Overview",
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <ReportsOverview />
                  </Suspense>
                ),
              },
              {
                path: "Subscription",
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <Subscription />
                  </Suspense>
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <PlansTable />
                      </Suspense>
                    ),
                  },
                  {
                    path: "checkout",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <Checkout />
                      </Suspense>
                    ),
                  },
                  {
                    path: "after-checkout",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <AfterCheckout />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: "Reports",
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <Reports />
                  </Suspense>
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <ReportsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "cash-flow",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <CashFlow />
                      </Suspense>
                    ),
                  },
                  {
                    path: "profit-loss",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <ProfitLoss />
                      </Suspense>
                    ),
                  },
                  {
                    path: "balance-sheet",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <BalanceSheet />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: "integrations",
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <Integrations />
                  </Suspense>
                ),
                children: [
                  {
                    index: true,
                    element: <Navigate to="/User/integrations/quickbooks" replace />,
                  },
                  {
                    path: "quickbooks",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <QuickBooksPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: "Settings",
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <UserSettings />
                  </Suspense>
                ),
                children: [],
              },
              {
                path: "QuickBooks-Reports",
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <QuickBooksReports />
                  </Suspense>
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <QuickBooksReportsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "cumulative",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <CumulativeReportsOverview />
                      </Suspense>
                    ),
                  },
                  {
                    path: "cumulative/profit-loss",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <CumulativeProfitLoss />
                      </Suspense>
                    ),
                  },
                  {
                    path: "cumulative/cash-flow",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <CumulativeCashFlow />
                      </Suspense>
                    ),
                  },
                  {
                    path: "cumulative/balance-sheet",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <CumulativeBalanceSheet />
                      </Suspense>
                    ),
                  },
                  {
                    path: ":reportType",
                    element: (
                      <Suspense fallback={
                        <div className="flex h-screen items-center justify-center">
                          <Spin
                            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                          />
                        </div>
                      }>
                        <QuickBooksReportDetailPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: "Home",
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <MainDashboard />
                  </Suspense>
                ),
                children: [],
              },
            ],
          },
        ]
      },
      {
        path: "Admin",
        element: (
          <ProtectedRoute>
            <RoleGuard>
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <Admin />
              </Suspense>
            </RoleGuard>
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <AdminAnalytics />
              </Suspense>
            ),
          },
          {
            path: 'Financials',
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <QuestionDashboard />
              </Suspense>
            ),
            children: [
              {
                path: ':title',
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <Questionnaire />
                  </Suspense>
                ),
              }
            ],
          },
          {
            path: 'Prompts',
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <PromptsDashboard />
              </Suspense>
            ),
            children: [
              {
                path: ':title',
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <Prompts />
                  </Suspense>
                ),
              }
            ],
          },
          {
            path: "Users",
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <AdminUsers />
              </Suspense>
            ),
            children: [
              {
                path: "Admins",
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <Admins />
                  </Suspense>
                ),
              },

              {
                path: "Moderators",
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <Moderators />
                  </Suspense>
                ),
              },
              {
                path: "Clients",
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <Clients />
                  </Suspense>
                ),
              },
              {
                path: "Client",
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <ClientStats />
                  </Suspense>
                ),
              },
              {
                path: "Client-Step/:title",
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <StepDetail />
                  </Suspense>
                ),
              }
            ],
          },
          {
            path: 'Blogs',
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <BlogsDashboard />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <Blogs />
                  </Suspense>
                ),
              },
              {
                path: ':title',
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <Blogs />
                  </Suspense>
                ),
              }
            ],
          },
        ],
      },
      {
        path: "Moderator",
        element: (
          <ProtectedRoute>
            <RoleGuard>
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <Moderator />
              </Suspense>
            </RoleGuard>
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <ModeratorAnalytics />
              </Suspense>
            ),
          },
          {
            path: "Users",
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <ModeratorUsers />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <ModeratorClients />
                  </Suspense>
                ),
              },
              {
                path: "Client",
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <ModeratorClientStats />
                  </Suspense>
                ),
              },
              {
                path: "Client-Step/:title",
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <ModeratorClientStep />
                  </Suspense>
                ),
              }
            ],
          },
          {
            path: 'Financials',
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <ModeratorQuestionnaire />
              </Suspense>
            ),
            children: [
              {
                path: ':title',
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <ModeratorQuestions />
                  </Suspense>
                ),
              }
            ],
          },
          {
            path: 'Prompts',
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <ModeratorPromptsDashboard />
              </Suspense>
            ),
            children: [
              {
                path: ':title',
                element: (
                  <Suspense fallback={
                    <div className="flex h-screen items-center justify-center">
                      <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                      />
                    </div>
                  }>
                    <ModeratorPrompts />
                  </Suspense>
                ),
              }
            ],
          },
          {
            path: 'Blogs',
            element: (
              <Suspense fallback={
                <div className="flex h-screen items-center justify-center">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                  />
                </div>
              }>
                <ModeratorBlogs />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <ErrorPage />,
  },
]);

export default router;
